import { DEFAULT_LANGUAGE, type Language } from '~/lib/language';
import * as accountsEn from './accounts';
import * as businessEn from './business';
import * as navigationEn from './navigation';
import { pricing as pricingEn } from './pricing';
import * as sharedEn from './shared';
import { ui as uiEn } from './ui';
import * as accountsAr from './ar/accounts';
import { AR_COPY_APPROVED } from './ar/approval';
import * as businessAr from './ar/business';
import * as navigationAr from './ar/navigation';
import { pricing as pricingAr } from './ar/pricing';
import * as sharedAr from './ar/shared';
import { ui as uiAr } from './ar/ui';

/**
 * Per-language content bundles.
 *
 * The English bundle is the source of truth for the shape: every other language
 * is typed as `ContentBundle`, so omitting a key or changing a type is a build
 * error rather than a silently-English string at runtime.
 *
 * Only translated modules are listed. Pages whose copy has not been localized yet
 * keep importing `~/content/<module>` directly and stay English — that is what
 * makes it possible to roll Arabic out page by page.
 */
export interface ContentBundle {
  accounts: {
    accountsHero: typeof accountsEn.accountsHero;
    accountsReceive: typeof accountsEn.accountsReceive;
    accountsTransfer: typeof accountsEn.accountsTransfer;
    accountsConvert: typeof accountsEn.accountsConvert;
  };
  shared: {
    trustedBySection: typeof sharedEn.trustedBySection;
    safeSpaceSection: typeof sharedEn.safeSpaceSection;
    investorsSection: typeof sharedEn.investorsSection;
    defaultTestimonials: typeof sharedEn.defaultTestimonials;
    defaultCtaBanner: typeof sharedEn.defaultCtaBanner;
    defaultFaq: typeof sharedEn.defaultFaq;
  };
  navigation: {
    primaryProducts: typeof navigationEn.primaryProducts;
    megaMenuColumns: typeof navigationEn.megaMenuColumns;
    headerNav: typeof navigationEn.headerNav;
    footerColumns: typeof navigationEn.footerColumns;
    mobileNav: typeof navigationEn.mobileNav;
    productsLabel: typeof navigationEn.productsLabel;
    getAppCta: typeof navigationEn.getAppCta;
    megaMenuPromo: typeof navigationEn.megaMenuPromo;
  };
  business: {
    businessHero: typeof businessEn.businessHero;
    businessReceive: typeof businessEn.businessReceive;
    businessInvoices: typeof businessEn.businessInvoices;
    businessPayTeam: typeof businessEn.businessPayTeam;
  };
  pricing: typeof pricingEn;
  ui: typeof uiEn;
}

const en: ContentBundle = {
  accounts: {
    accountsHero: accountsEn.accountsHero,
    accountsReceive: accountsEn.accountsReceive,
    accountsTransfer: accountsEn.accountsTransfer,
    accountsConvert: accountsEn.accountsConvert,
  },
  shared: {
    trustedBySection: sharedEn.trustedBySection,
    safeSpaceSection: sharedEn.safeSpaceSection,
    investorsSection: sharedEn.investorsSection,
    defaultTestimonials: sharedEn.defaultTestimonials,
    defaultCtaBanner: sharedEn.defaultCtaBanner,
    defaultFaq: sharedEn.defaultFaq,
  },
  navigation: {
    primaryProducts: navigationEn.primaryProducts,
    megaMenuColumns: navigationEn.megaMenuColumns,
    headerNav: navigationEn.headerNav,
    footerColumns: navigationEn.footerColumns,
    mobileNav: navigationEn.mobileNav,
    productsLabel: navigationEn.productsLabel,
    getAppCta: navigationEn.getAppCta,
    megaMenuPromo: navigationEn.megaMenuPromo,
  },
  business: {
    businessHero: businessEn.businessHero,
    businessReceive: businessEn.businessReceive,
    businessInvoices: businessEn.businessInvoices,
    businessPayTeam: businessEn.businessPayTeam,
  },
  pricing: pricingEn,
  ui: uiEn,
};

/**
 * Arabic bundle.
 *
 * Three areas fall back to the approved English copy while their sign-off is
 * outstanding — see `ar/approval.ts` for what each one is waiting on. The
 * fallback is deliberate: an English cookie banner on an Arabic page is a
 * visible gap, whereas an unapproved Arabic disclosure is a compliance problem.
 */
const ar: ContentBundle = {
  accounts: {
    accountsHero: accountsAr.accountsHero,
    accountsReceive: accountsAr.accountsReceive,
    accountsTransfer: accountsAr.accountsTransfer,
    accountsConvert: accountsAr.accountsConvert,
  },
  shared: {
    trustedBySection: sharedAr.trustedBySection,
    safeSpaceSection: sharedAr.safeSpaceSection,
    investorsSection: sharedAr.investorsSection,
    defaultTestimonials: sharedAr.defaultTestimonials,
    defaultCtaBanner: sharedAr.defaultCtaBanner,
    defaultFaq: AR_COPY_APPROVED.faq ? sharedAr.defaultFaq : sharedEn.defaultFaq,
  },
  navigation: {
    primaryProducts: navigationAr.primaryProducts,
    megaMenuColumns: navigationAr.megaMenuColumns,
    headerNav: navigationAr.headerNav,
    footerColumns: navigationAr.footerColumns,
    mobileNav: navigationAr.mobileNav,
    productsLabel: navigationAr.productsLabel,
    getAppCta: navigationAr.getAppCta,
    megaMenuPromo: navigationAr.megaMenuPromo,
  },
  business: {
    businessHero: businessAr.businessHero,
    businessReceive: businessAr.businessReceive,
    businessInvoices: businessAr.businessInvoices,
    businessPayTeam: businessAr.businessPayTeam,
  },
  pricing: pricingAr,
  ui: {
    ...uiAr,
    cookies: AR_COPY_APPROVED.cookies ? uiAr.cookies : uiEn.cookies,
    legal: AR_COPY_APPROVED.legal ? uiAr.legal : uiEn.legal,
  },
};

const bundles: Record<Language, ContentBundle> = { en, ar };

export function contentFor(language: Language): ContentBundle {
  return bundles[language] ?? bundles[DEFAULT_LANGUAGE];
}
